// Modal-state reducer for `DashboardContext` (H1c-ADR-001 Sub-decision D).
//
// Extracted from DashboardContext.tsx so the pure transition table can be
// imported from tests without dragging in the React / next-intl /
// fetchWithAuth side-imports of the provider module. The DashboardContext
// re-exports `modalReducer`, `ModalState`, and `ModalAction` so consumers
// have one canonical import path; new tests target this module directly.

import type { Project as ProjectSummary } from '@/types/project';
import type { ProjectFolder, ProjectFolderVisibility } from '@/components/home/theme-editor';

export type ModalState =
  | { kind: 'none' }
  | { kind: 'delete'; project: ProjectSummary }
  | { kind: 'rename'; project: ProjectSummary; value: string }
  | {
      kind: 'createFolder';
      mode: 'create' | 'edit';
      target: ProjectFolder | null;
      name: string;
      visibility: ProjectFolderVisibility;
    }
  | { kind: 'deleteFolder'; folder: ProjectFolder }
  | { kind: 'addProjects'; folder: ProjectFolder }
  | { kind: 'createSubfolder'; folder: ProjectFolder }
  | { kind: 'moveFolder'; folder: ProjectFolder }
  | { kind: 'contextMenu'; project: ProjectSummary; x: number; y: number; view: 'main' };

export type ModalAction =
  | { type: 'OPEN_DELETE'; project: ProjectSummary }
  | { type: 'OPEN_RENAME'; project: ProjectSummary }
  | { type: 'SET_RENAME_VALUE'; value: string }
  | { type: 'OPEN_CREATE_FOLDER' }
  | { type: 'OPEN_EDIT_FOLDER'; folder: ProjectFolder }
  | { type: 'SET_CREATE_FOLDER_NAME'; name: string }
  | { type: 'SET_CREATE_FOLDER_VISIBILITY'; visibility: ProjectFolderVisibility }
  | { type: 'OPEN_DELETE_FOLDER'; folder: ProjectFolder }
  | { type: 'OPEN_ADD_PROJECTS'; folder: ProjectFolder }
  | { type: 'OPEN_CREATE_SUBFOLDER'; folder: ProjectFolder }
  | { type: 'OPEN_MOVE_FOLDER'; folder: ProjectFolder }
  | { type: 'OPEN_CONTEXT_MENU'; project: ProjectSummary; x: number; y: number }
  | { type: 'CLOSE' }
  | { type: 'WORKSPACE_CHANGED' };

export const INITIAL_MODAL_STATE: ModalState = { kind: 'none' };

export function modalReducer(state: ModalState, action: ModalAction): ModalState {
  switch (action.type) {
    case 'OPEN_DELETE':
      return { kind: 'delete', project: action.project };
    case 'OPEN_RENAME':
      return { kind: 'rename', project: action.project, value: action.project.name };
    case 'SET_RENAME_VALUE':
      // Tolerated when modal is not in 'rename' kind: ignored. Keeps the action
      // total without forcing callers to assert the current kind.
      if (state.kind !== 'rename') return state;
      return { ...state, value: action.value };
    case 'OPEN_CREATE_FOLDER':
      return {
        kind: 'createFolder',
        mode: 'create',
        target: null,
        name: '',
        visibility: 'workspace',
      };
    case 'OPEN_EDIT_FOLDER':
      return {
        kind: 'createFolder',
        mode: 'edit',
        target: action.folder,
        name: action.folder.name,
        visibility: action.folder.visibility,
      };
    case 'SET_CREATE_FOLDER_NAME':
      if (state.kind !== 'createFolder') return state;
      return { ...state, name: action.name };
    case 'SET_CREATE_FOLDER_VISIBILITY':
      if (state.kind !== 'createFolder') return state;
      return { ...state, visibility: action.visibility };
    case 'OPEN_DELETE_FOLDER':
      return { kind: 'deleteFolder', folder: action.folder };
    case 'OPEN_ADD_PROJECTS':
      return { kind: 'addProjects', folder: action.folder };
    case 'OPEN_CREATE_SUBFOLDER':
      return { kind: 'createSubfolder', folder: action.folder };
    case 'OPEN_MOVE_FOLDER':
      return { kind: 'moveFolder', folder: action.folder };
    case 'OPEN_CONTEXT_MENU':
      return { kind: 'contextMenu', project: action.project, x: action.x, y: action.y, view: 'main' };
    case 'CLOSE':
    case 'WORKSPACE_CHANGED':
      return INITIAL_MODAL_STATE;
    default:
      // Exhaustiveness fallback. The discriminated union should make this
      // unreachable; keeping it returns the same state if a future action is
      // dispatched before the reducer is updated.
      return state;
  }
}
